// Utility function for debouncing
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Utility function to handle Escape key events
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() === 'ESCAPE') {
    // Close any open facet filters or modals
    const openFacets = document.querySelectorAll('.facets-container .facets__form:not(.hidden)');
    openFacets.forEach((facet) => {
      facet.classList.add('hidden');
    });

    // Remove focus from any focused elements within facets
    const focusedElement = document.activeElement;
    if (focusedElement && focusedElement.closest('.facets-container')) {
      focusedElement.blur();
    }
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    console.log('FacetFiltersForm.renderPage called', {
      searchParams: searchParams,
      searchParamsType: typeof searchParams,
      searchParamsLength: searchParams ? searchParams.length : 'N/A',
      event: event,
      updateURLHash: updateURLHash,
      searchParamsPrev: FacetFiltersForm.searchParamsPrev,
    });

    // Handle case where searchParams might be undefined or empty
    if (!searchParams) {
      console.log('searchParams is falsy, setting to empty string');
      searchParams = '';
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    if (!sections || sections.length === 0) {
      console.error('No sections found to render');
      return;
    }

    console.log('Found sections:', sections);

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collectionElement = productGridContainer.querySelector('.collection');
      if (collectionElement) {
        collectionElement.classList.add('loading');
      }
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      console.log('Fetching section URL:', {
        pathname: window.location.pathname,
        sectionId: section.section,
        searchParams: searchParams,
        fullUrl: url,
        searchParamsEmpty: !searchParams || searchParams.length === 0,
      });

      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      console.log('Updating URL hash with:', searchParams);
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event) {
    console.log('FacetFiltersForm.renderSectionFromFetch called', {
      url: url,
      event: event,
    });

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        console.log('Received HTML response, length:', responseText.length);

        // Let's inspect the HTML response for active facets
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = responseText;
        const activeFacetsInResponse = tempDiv.querySelectorAll('.active-facets-mobile, .active-facets-desktop');
        console.log('Active facets found in HTML response:', {
          count: activeFacetsInResponse.length,
          elements: Array.from(activeFacetsInResponse).map((el) => ({
            className: el.className,
            innerHTML: el.innerHTML.substring(0, 200) + '...',
            hasFacetRemove: el.querySelector('facet-remove') !== null,
          })),
        });

        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        console.log('Rendering filters from fetched HTML');
        FacetFiltersForm.renderFilters(html, event);

        console.log('Rendering product grid container');
        FacetFiltersForm.renderProductGridContainer(html);

        console.log('Rendering product count');
        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        console.error('Error fetching section:', error);
        // Remove loading states on error
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) {
          const collectionElement = productGridContainer.querySelector('.collection');
          if (collectionElement) {
            collectionElement.classList.remove('loading');
          }
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) {
      console.error('ProductGridContainer not found');
      return;
    }

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newProductGridContainer = parsedHTML.getElementById('ProductGridContainer');

    if (!newProductGridContainer) {
      console.error('ProductGridContainer not found in parsed HTML');
      return;
    }

    productGridContainer.innerHTML = newProductGridContainer.innerHTML;

    // Remove loading state
    const collectionElement = productGridContainer.querySelector('.collection');
    if (collectionElement) {
      collectionElement.classList.remove('loading');
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount')?.innerHTML;

    if (!count) return;

    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    console.log('FacetFiltersForm.renderFilters called', {
      htmlLength: html.length,
      event: event,
    });

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    console.log('Filter elements found:', {
      fromFetch: facetDetailsElementsFromFetch.length,
      fromDom: facetDetailsElementsFromDom.length,
    });

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        console.log('Removing facet element:', currentElement.id);
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    console.log('Facets to render:', {
      facetsToRender: facetsToRender.length,
      countsToRender: countsToRender ? countsToRender.id : 'none',
    });

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        console.log('Updating existing facet element:', elementToRender.id);
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            console.log('Inserting facet element after:', previousElementId);
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          console.log('Inserting facet element before parent:', elementToRender.parentElement.id);
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    console.log('Rendering active facets');
    FacetFiltersForm.renderActiveFacets(parsedHTML);

    console.log('Rendering additional elements');
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        console.log('Rendering counts for filter:', closestJSFilterID);
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    console.log('FacetFiltersForm.renderActiveFacets called', {
      htmlLength: html.length,
      htmlType: typeof html,
      htmlKeys: html instanceof Element ? Object.keys(html) : 'Not an Element',
    });

    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) {
        console.log('No active facets element found for selector:', selector);
        // Let's see what's actually in the HTML
        console.log('Available elements in HTML:', {
          allElements: html.querySelectorAll('*').length,
          activeFacetsElements: html.querySelectorAll('[class*="active-facets"]').length,
          facetElements: html.querySelectorAll('[class*="facet"]').length,
        });
        return;
      }

      const existingElement = document.querySelector(selector);
      if (existingElement) {
        console.log('Updating active facets for selector:', selector, {
          oldContent: existingElement.innerHTML.substring(0, 100) + '...',
          newContent: activeFacetsElement.innerHTML.substring(0, 100) + '...',
          oldContentLength: existingElement.innerHTML.length,
          newContentLength: activeFacetsElement.innerHTML.length,
        });

        // Log the full content for debugging
        console.log('Full old content:', existingElement.innerHTML);
        console.log('Full new content:', activeFacetsElement.innerHTML);

        existingElement.innerHTML = activeFacetsElement.innerHTML;
      } else {
        console.log('Active facets element not found in DOM for selector:', selector);
      }
    });

    console.log('Toggling active facets (disabled: false)');
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // Safely call bindEvents if it exists
    try {
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm) {
        const menuDrawer = mobileForm.closest('menu-drawer');
        if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
          menuDrawer.bindEvents();
        } else {
          console.log('bindEvents method not available on menu-drawer element');
        }
      }
    } catch (error) {
      // Error handling for bindEvents
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement.classList.add('more-shown');
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    var popupIcons = document.querySelectorAll('.popup-icon');
    // Loop through each popup icon and add click event
    popupIcons.forEach(function (popupIcon) {
      popupIcon.addEventListener('click', function (event) {
        if (window.innerWidth > 768) {
          event.preventDefault();
        }
        // Get the associated popup ID from the data attribute
        var popupId = this.getAttribute('data-popup-id');
        var popup = document.querySelector('.popupp[data-popup-id="' + popupId + '"]');
        // Show the associated popup
        if (popup) {
          popup.classList.remove('hidden');
        }
      });
    });

    // Get all close buttons
    var closePopupBtns = document.querySelectorAll('.closePopup');

    // Loop through each close button and add click event
    closePopupBtns.forEach(function (closePopupBtn) {
      closePopupBtn.addEventListener('click', function (event) {
        event.preventDefault();

        // Get the parent popup using the closest method
        var popup = this.closest('.popupp');

        // Hide the popup
        if (popup) {
          popup.classList.add('hidden');
        }
      });
    });
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const newURL = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    history.pushState({ searchParams }, '', newURL);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');

    if (!productGrid) {
      console.warn('Product grid element not found, using fallback section ID');
      return [
        {
          section: 'main-collection-product-grid',
        },
      ];
    }

    const sectionId = productGrid.dataset.id;
    console.log('Section ID from data-id:', sectionId);

    if (!sectionId) {
      console.warn('No data-id found on product-grid, using fallback section ID');
      return [
        {
          section: 'main-collection-product-grid',
        },
      ];
    }

    return [
      {
        section: sectionId,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    // Only include checked checkboxes - Shopify will handle the default state
    // for unchecked filters automatically
    const params = new URLSearchParams(formData).toString();
    return params;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // Get the form that contains the changed input
    let targetElement = event.target;
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      // Define sortFilterForms - get all forms within facet-filters-form elements
      const sortFilterForms = document.querySelectorAll('facet-filters-form form');

      sortFilterForms.forEach((form, index) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const formParams = this.createSearchParams(form);
            forms.push(formParams);
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          const formParams = this.createSearchParams(form);
          forms.push(formParams);
        }
      });

      const combinedParams = forms.join('&');
      this.onSubmitForm(combinedParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    console.log('FacetFiltersForm.onActiveFilterClick called', {
      event: event,
      currentTarget: event.currentTarget,
      href: event.currentTarget.href,
    });

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    console.log('Processed URL for removal:', {
      originalHref: event.currentTarget.href,
      extractedUrl: url,
      willRenderPage: true,
    });

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );

    this.setMinAndMaxValues();

    this.querySelectorAll('.range-wrap').forEach((wrap) => {
      const range = wrap.querySelector('.range');
      const bubble = wrap.querySelector('.bubble');

      range.addEventListener('input', () => {
        this.setMinAndMaxValues();
        this.setBubble(range, bubble);
      });
      this.setBubble(range, bubble);
    });
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
  }

  setBubble(range, bubble) {
    const val = range.value;
    const min = range.min ? range.min : 0;
    const max = range.max ? range.max : 100;
    const newVal = Number(((val - min) * 100) / (max - min));

    const parentBubble = bubble.parentElement;
    const bubbleOutput = parentBubble.querySelector('.bubble-output');

    if (bubbleOutput) {
      bubbleOutput.innerHTML = `€${val.replace(/\./g, ',')}`;
    }

    // Sorta magic numbers based on size of the native UI thumb
    bubble.style.left = `calc(${newVal}% + (${8 - newVal * 0.15}px))`;
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    console.log('FacetRemove.closeFilter called', {
      event: event,
      currentTarget: event.currentTarget,
      href: event.currentTarget.href,
      urlToRemove: event.currentTarget.href,
      currentUrl: window.location.href,
      searchParams: window.location.search,
    });

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    console.log('Found form:', form);

    if (form && typeof form.onActiveFilterClick === 'function') {
      console.log('Calling onActiveFilterClick with event:', event);
      form.onActiveFilterClick(event);
    } else {
      console.error('Form or onActiveFilterClick method not found');
    }
  }
}

customElements.define('facet-remove', FacetRemove);

document.addEventListener('DOMContentLoaded', function () {
  // Function to handle checkbox text clicks
  function initializeFacetTextClicks() {
    // Select all facet checkbox text elements
    const facetTexts = document.querySelectorAll('.facet-checkbox__text');

    facetTexts.forEach((text) => {
      // Remove any existing listeners to avoid duplicates
      text.style.cursor = 'pointer';

      text.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        // Find the closest parent with data-fake-label
        const fakeLabel = this.closest('[data-fake-label]');
        if (fakeLabel) {
          // Find the checkbox within this fake label
          const checkbox = fakeLabel.querySelector('input[type="checkbox"]');
          if (checkbox && !checkbox.disabled) {
            // Toggle the checkbox
            checkbox.checked = !checkbox.checked;

            // Trigger the change event to ensure filters update
            const changeEvent = new Event('input', {
              bubbles: true,
              cancelable: true,
            });
            checkbox.dispatchEvent(changeEvent);
          }
        }
      });
    });
  }

  // Initialize on page load
  initializeFacetTextClicks();

  // Re-initialize after facets are rendered
  const originalRenderFilters = FacetFiltersForm.renderFilters;
  FacetFiltersForm.renderFilters = function (html, event) {
    originalRenderFilters.call(this, html, event);
    // Re-initialize click handlers after filters are re-rendered
    setTimeout(initializeFacetTextClicks, 100);
  };
});
